import {
  CalendarClock,
  Check,
  CircleDot,
  Fuel,
  Hand,
  Info,
  Lightbulb,
  Plane,
  Plus,
  Stethoscope,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useIntl } from '@/core/i18n/use-intl';
import type { StatusColor, StatusOption, StatusRange, StatusValue } from '@/shared/schema';
import { getCurrentStatusResolution } from '@/shared/utils/status';
import { Button } from '../../button';
import { Card, CardContent } from '../../card';
import { DatePicker } from '../../date-picker';
import { FormItem } from '../../form';
import { Label } from '../../label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../select';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../tooltip';
import AutoFormLabel from '../common/label';
import type { AutoFormInputComponentProps } from '../types';

const ensureStatusOptionsWithIds = (
  options: StatusOption[]
): Array<StatusOption & { id: string }> =>
  options.map((option, index) => {
    if (option.id && option.id.trim() !== '') {
      return {
        ...option,
        id: option.id,
      };
    }

    return {
      ...option,
      id: `status-opt-${option.color}-${index + 1}`,
    };
  });

// Icon mapping
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  fuel: Fuel,
  helideck: Plane,
  warning: TriangleAlert,
  check: Check,
  lightning: Lightbulb,
  medical: Stethoscope,
};

// Color classes for status indicators
const colorClasses: Record<StatusColor, string> = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
};

const colorTextClasses: Record<StatusColor, string> = {
  green: 'text-green-600 dark:text-green-400',
  yellow: 'text-yellow-600 dark:text-yellow-400',
  red: 'text-red-600 dark:text-red-400',
};

// Selected state uses shadcn primary colors for better visibility
const selectedButtonClass = 'border-primary bg-primary text-primary-foreground';
const unselectedButtonClass =
  'border-border bg-background hover:bg-accent hover:text-accent-foreground';

interface RangeValidationError {
  type: 'incomplete' | 'endBeforeStart' | 'overlap';
  message: string;
}

// Helper function to check if two ranges overlap
function rangesOverlap(
  rangeA: { startDate: string; endDate: string },
  rangeB: { startDate: string; endDate: string }
): boolean {
  if (!rangeA.startDate || !rangeA.endDate || !rangeB.startDate || !rangeB.endDate) {
    return false;
  }
  const startA = new Date(rangeA.startDate);
  const endA = new Date(rangeA.endDate);
  const startB = new Date(rangeB.startDate);
  const endB = new Date(rangeB.endDate);

  // Two ranges overlap if: startA <= endB && startB <= endA
  return startA <= endB && startB <= endA;
}

interface StatusRangeRowProps {
  range: StatusRange;
  index: number;
  statusOptions: StatusOption[];
  statusOptionsByColor: Record<StatusColor, StatusOption[]>;
  onUpdate: (id: string, updates: Partial<StatusRange>) => void;
  onRemove: (id: string) => void;
  icon?: string;
  disabled?: boolean;
  validationError?: RangeValidationError;
  disabledDates?: Array<{ start: Date; end: Date }>;
  compact?: boolean;
}

function StatusRangeRow({
  range,
  index,
  statusOptions,
  statusOptionsByColor,
  onUpdate,
  onRemove,
  icon,
  disabled,
  validationError,
  disabledDates,
  compact,
}: StatusRangeRowProps) {
  const { t, locale } = useIntl();
  const IconComponent = icon ? iconMap[icon] : null;

  const getLabel = (opt: StatusOption) =>
    locale === 'de-DE' ? opt.labelDe : opt.labelEn || opt.labelDe;

  const selectedStatusDuringOptionId = useMemo(() => {
    if (range.statusDuringOptionId) return range.statusDuringOptionId;
    const fallback = statusOptionsByColor[range.statusDuring]?.[0];
    return fallback?.id || `${range.statusDuring}`;
  }, [range.statusDuring, range.statusDuringOptionId, statusOptionsByColor]);

  const selectedFallbackOptionValue = useMemo(() => {
    if (!range.fallbackAfter) return 'none';
    if (range.fallbackAfterOptionId) return range.fallbackAfterOptionId;
    const fallback = statusOptionsByColor[range.fallbackAfter]?.[0];
    return fallback?.id || `${range.fallbackAfter}`;
  }, [range.fallbackAfter, range.fallbackAfterOptionId, statusOptionsByColor]);

  const resolveOptionByValue = (value: string): StatusOption | null => {
    const byId = statusOptions.find((opt) => opt.id === value);
    if (byId) return byId;

    if (value === 'red' || value === 'yellow' || value === 'green') {
      return statusOptionsByColor[value]?.[0] ?? null;
    }

    return null;
  };

  // Calculate min date for end date (must be >= start date)
  const minEndDate = range.startDate ? new Date(`${range.startDate}T12:00:00`) : undefined;

  const hasError = !!validationError;
  const borderClass = hasError ? 'border-destructive bg-destructive/5' : 'border bg-muted/30';

  return (
    <div
      className={`grid gap-2 p-2 rounded-lg ${borderClass}`}
      data-testid={`status-range-row-${index}`}
    >
      {validationError && (
        <p className="text-xs text-destructive" data-testid={`range-${index}-error`}>
          {validationError.message}
        </p>
      )}

      {/* Desktop: single-line row layout; Mobile: stacked with labels */}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_1fr_1fr_auto] md:items-end">
        <div className="space-y-1" data-testid={`range-${index}-start-date`}>
          <Label className={compact ? 'text-xs md:sr-only' : 'text-xs'}>
            {t('metadata.status.rangeStartDate')}
            <span className="text-destructive"> *</span>
          </Label>
          <DatePicker
            date={range.startDate ? new Date(`${range.startDate}T12:00:00`) : undefined}
            setDate={(date) => {
              const dateString = date
                ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
                    2,
                    '0'
                  )}-${String(date.getDate()).padStart(2, '0')}`
                : '';
              onUpdate(range.id, { startDate: dateString });
            }}
            disabled={disabled}
            disabledRanges={disabledDates}
          />
        </div>

        <div className="space-y-1" data-testid={`range-${index}-end-date`}>
          <Label className={compact ? 'text-xs md:sr-only' : 'text-xs'}>
            {t('metadata.status.rangeEndDate')}
            <span className="text-destructive"> *</span>
          </Label>
          <DatePicker
            date={range.endDate ? new Date(`${range.endDate}T12:00:00`) : undefined}
            setDate={(date) => {
              const dateString = date
                ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
                    2,
                    '0'
                  )}-${String(date.getDate()).padStart(2, '0')}`
                : '';
              onUpdate(range.id, { endDate: dateString });
            }}
            disabled={disabled}
            minDate={minEndDate}
            disabledRanges={disabledDates}
          />
        </div>

        <div className="space-y-1">
          <Label className={compact ? 'text-xs md:sr-only' : 'text-xs'}>
            {t('metadata.status.statusDuring')}
            <span className="text-destructive"> *</span>
          </Label>
          <Select
            value={selectedStatusDuringOptionId}
            onValueChange={(v) => {
              const option = resolveOptionByValue(v);
              if (!option) return;
              onUpdate(range.id, {
                statusDuring: option.color,
                statusDuringOptionId: option.id,
              });
            }}
            disabled={disabled}
          >
            <SelectTrigger data-testid={`range-${index}-status-during`} className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((opt) => (
                <SelectItem
                  key={opt.id || `${opt.color}-${opt.labelDe}`}
                  value={opt.id || opt.color}
                >
                  <div className="flex items-center gap-2">
                    {IconComponent ? (
                      <IconComponent className={`size-4 ${colorTextClasses[opt.color]}`} />
                    ) : (
                      <span className={`w-2 h-2 rounded-full ${colorClasses[opt.color]}`} />
                    )}
                    <span>{getLabel(opt)}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className={compact ? 'text-xs md:sr-only' : 'text-xs'}>
            {t('metadata.status.fallbackAfter')}
          </Label>
          <Select
            value={selectedFallbackOptionValue}
            onValueChange={(v) =>
              onUpdate(
                range.id,
                v === 'none'
                  ? {
                      fallbackAfter: undefined,
                      fallbackAfterOptionId: undefined,
                    }
                  : (() => {
                      const option = resolveOptionByValue(v);
                      if (!option) {
                        return {
                          fallbackAfter: undefined,
                          fallbackAfterOptionId: undefined,
                        };
                      }
                      return {
                        fallbackAfter: option.color,
                        fallbackAfterOptionId: option.id,
                      };
                    })()
              )
            }
            disabled={disabled}
          >
            <SelectTrigger data-testid={`range-${index}-fallback-after`} className="h-9">
              <SelectValue placeholder={t('metadata.status.fallbackAfterOptional')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <span className="text-muted-foreground">-</span>
              </SelectItem>
              {statusOptions.map((opt) => (
                <SelectItem
                  key={opt.id || `${opt.color}-${opt.labelDe}`}
                  value={opt.id || opt.color}
                >
                  <div className="flex items-center gap-2">
                    {IconComponent ? (
                      <IconComponent className={`size-4 ${colorTextClasses[opt.color]}`} />
                    ) : (
                      <span className={`w-2 h-2 rounded-full ${colorClasses[opt.color]}`} />
                    )}
                    <span>{getLabel(opt)}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-end justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRemove(range.id)}
            disabled={disabled}
            data-testid={`range-${index}-remove`}
            className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
            aria-label={t('metadata.status.removeRange')}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

interface CurrentStatusIndicatorProps {
  statusValue: StatusValue;
  statusOptions: StatusOption[];
  getLabel: (opt: StatusOption) => string;
  icon?: string;
}

/**
 * Displays the current computed status for scheduled mode.
 * This helps users verify the time-based status logic is working correctly.
 */
function CurrentStatusIndicator({
  statusValue,
  statusOptions,
  getLabel,
  icon,
}: CurrentStatusIndicatorProps) {
  // Compute current status based on the status value configuration
  const currentResolution = useMemo(() => {
    return getCurrentStatusResolution(statusValue);
  }, [statusValue]);
  const currentStatus = currentResolution.color;

  // Find the matching status option
  const currentStatusOption =
    statusOptions.find((opt) => opt.id === currentResolution.optionId) ||
    statusOptions.find((opt) => opt.color === currentStatus);
  const currentStatusLabel = currentStatusOption ? getLabel(currentStatusOption) : currentStatus;
  const IconComponent = icon ? iconMap[icon] : null;

  return (
    <div
      className="flex items-center gap-3 p-2 rounded-lg bg-muted/40 border border-border/60"
      data-testid="current-status-indicator"
    >
      <div className="flex items-center gap-2">
        {IconComponent ? (
          <IconComponent
            className={`size-4 ${colorTextClasses[currentStatus]}`}
            data-testid={`current-status-indicator-icon-${currentStatus}`}
          />
        ) : (
          <span className={`w-2.5 h-2.5 rounded-full ${colorClasses[currentStatus]}`} />
        )}
        <span className="text-sm font-medium">{currentStatusLabel}</span>
      </div>
    </div>
  );
}

export default function AutoFormStatus({
  label,
  isRequired,
  fieldConfigItem,
  field,
  fieldProps,
}: AutoFormInputComponentProps & {
  field: ControllerRenderProps<FieldValues, string>;
}) {
  const { t, locale } = useIntl();
  const { getValues, setValue, formState } = useFormContext();
  const { showLabel: _showLabel, disabled, ...restFieldProps } = fieldProps;
  const showLabel = _showLabel === undefined ? true : _showLabel;
  const fieldPath = field.name;

  // Track whether validation errors should be shown
  // Only show errors after the form has been submitted at least once
  const shouldShowValidationErrors = formState.submitCount > 0;

  // Extract status options from field config
  const rawStatusOptions: StatusOption[] = (fieldConfigItem?.statusOptions as StatusOption[]) || [
    { id: 'status-opt-green', color: 'green', labelDe: 'Aktiv', labelEn: 'Active' },
    { id: 'status-opt-red', color: 'red', labelDe: 'Inaktiv', labelEn: 'Inactive' },
  ];
  const statusOptions = useMemo(
    () => ensureStatusOptionsWithIds(rawStatusOptions),
    [rawStatusOptions]
  );
  const statusOptionsByColor = useMemo(
    () => ({
      green: statusOptions.filter((opt) => opt.color === 'green'),
      yellow: statusOptions.filter((opt) => opt.color === 'yellow'),
      red: statusOptions.filter((opt) => opt.color === 'red'),
    }),
    [statusOptions]
  );
  const statusOptionTestIds = useMemo(() => {
    const variantCounter: Record<StatusColor, number> = {
      green: 0,
      yellow: 0,
      red: 0,
    };

    return statusOptions.map((option) => {
      variantCounter[option.color] += 1;
      const variantNumber = variantCounter[option.color];

      if (variantNumber === 1) {
        return `status-option-opt-${option.color}`;
      }

      return `status-option-${option.id || `opt-${option.color}-${variantNumber}`}`;
    });
  }, [statusOptions]);
  const icon = fieldConfigItem?.icon as string | undefined;
  const IconComponent = icon ? iconMap[icon] : null;

  // Get label for status option
  const getLabel = (opt: StatusOption) =>
    locale === 'de-DE' ? opt.labelDe : opt.labelEn || opt.labelDe;

  // Default status value
  const defaultStatusValue: StatusValue = {
    mode: 'manual',
    manualStatus: statusOptions[0]?.color || 'green',
    manualStatusOptionId: statusOptions[0]?.id,
    defaultStatus: statusOptions[0]?.color || 'green',
    defaultStatusOptionId: statusOptions[0]?.id,
  };

  // Check if field already has a valid value
  const existingFieldValue = field.value as StatusValue | undefined;
  const hasExistingValue =
    existingFieldValue && typeof existingFieldValue === 'object' && existingFieldValue.mode;

  // Local state - initialize from field.value or form
  const [statusValue, setStatusValue] = useState<StatusValue>(() => {
    if (hasExistingValue) {
      return existingFieldValue;
    }
    // Fallback to getValues
    const formValue = getValues(fieldPath) as StatusValue | undefined;
    if (formValue && typeof formValue === 'object' && formValue.mode) {
      return formValue;
    }
    return defaultStatusValue;
  });

  // Sync from field.value when it changes externally (e.g., form reset)
  useEffect(() => {
    const fieldValue = field.value as StatusValue | undefined;
    if (fieldValue && typeof fieldValue === 'object' && fieldValue.mode) {
      // Only update if the value is fundamentally different (external change)
      // Don't update on every render to avoid overwriting local changes
      const currentMode = statusValue.mode;
      const newMode = fieldValue.mode;
      if (currentMode !== newMode) {
        setStatusValue(fieldValue);
      }
    }
  }, [field.value]); // Only depend on field.value, not statusValue

  // Check if there's currently a form-level error for this field
  const hasFormError = (() => {
    const pathParts = fieldPath.split('.');
    let current: Record<string, unknown> = formState.errors as Record<string, unknown>;
    for (const part of pathParts) {
      if (!current || typeof current !== 'object') return false;
      current = current[part] as Record<string, unknown>;
    }
    return current !== undefined && current !== null;
  })();

  // Sync to form - store all ranges (including incomplete) for form state
  // Validation will happen on form submit via zod schema
  const syncToForm = useCallback(
    (newValue: StatusValue) => {
      // Create the value for the form - include all ranges to maintain UI state
      const formValue: StatusValue = {
        mode: newValue.mode,
        defaultStatus: newValue.defaultStatus,
      };

      if (newValue.mode === 'manual' && newValue.manualStatus) {
        formValue.manualStatus = newValue.manualStatus;
        if (newValue.manualStatusOptionId) {
          formValue.manualStatusOptionId = newValue.manualStatusOptionId;
        }
      }

      if (newValue.defaultStatusOptionId) {
        formValue.defaultStatusOptionId = newValue.defaultStatusOptionId;
      }

      if (newValue.mode === 'scheduled' && newValue.ranges) {
        // Store all ranges (even incomplete ones) for proper UI state
        // But mark incomplete ranges so form validation can reject them
        formValue.ranges = newValue.ranges;
      }

      // Use setValue for reliable form state update
      // If there's an existing error, trigger validation to potentially clear it
      // Otherwise don't validate (validation happens on form submit)
      setValue(fieldPath, formValue, {
        shouldDirty: true,
        shouldValidate: hasFormError,
      });
    },
    [fieldPath, setValue, hasFormError]
  );

  // Sync statusValue to form whenever it changes
  // This ensures the form always has the latest sanitized value
  useEffect(() => {
    syncToForm(statusValue);
  }, [statusValue, syncToForm]);

  // Handle mode change
  const handleModeChange = useCallback((mode: 'manual' | 'scheduled') => {
    setStatusValue((prev) => ({ ...prev, mode }));
  }, []);

  // Handle manual status change
  const handleManualStatusChange = useCallback((option: StatusOption) => {
    setStatusValue((prev) => ({
      ...prev,
      manualStatus: option.color,
      manualStatusOptionId: option.id,
    }));
  }, []);

  // Handle default status change
  const handleDefaultStatusChange = useCallback((option: StatusOption) => {
    setStatusValue((prev) => ({
      ...prev,
      defaultStatus: option.color,
      defaultStatusOptionId: option.id,
    }));
  }, []);

  // Validate ranges and calculate disabled dates for each range
  const rangeValidation = useMemo(() => {
    const errors: Map<string, RangeValidationError> = new Map();
    const disabledDatesForRange: Map<string, Array<{ start: Date; end: Date }>> = new Map();

    if (!statusValue.ranges || statusValue.ranges.length === 0) {
      return { errors, disabledDatesForRange };
    }

    for (let i = 0; i < statusValue.ranges.length; i++) {
      const range = statusValue.ranges[i];

      // Calculate disabled dates for this range FIRST (other ranges' date spans)
      // This must happen regardless of validation errors so new/incomplete ranges
      // still show the correct disabled dates from other complete ranges
      const disabledRanges: Array<{ start: Date; end: Date }> = [];
      for (let j = 0; j < statusValue.ranges.length; j++) {
        if (i === j) continue;
        const otherRange = statusValue.ranges[j];
        if (otherRange.startDate && otherRange.endDate) {
          disabledRanges.push({
            start: new Date(`${otherRange.startDate}T00:00:00`),
            end: new Date(`${otherRange.endDate}T23:59:59`),
          });
        }
      }
      disabledDatesForRange.set(range.id, disabledRanges);

      // Check for incomplete range (missing any required field)
      const hasStartDate = !!range.startDate;
      const hasEndDate = !!range.endDate;
      const hasStatusDuring = !!range.statusDuring;

      if (!hasStartDate || !hasEndDate || !hasStatusDuring) {
        errors.set(range.id, {
          type: 'incomplete',
          message: t('metadata.status.rangeIncompleteError'),
        });
        continue;
      }

      // Check if end date is before start date
      if (range.startDate && range.endDate) {
        const start = new Date(range.startDate);
        const end = new Date(range.endDate);
        if (end < start) {
          errors.set(range.id, {
            type: 'endBeforeStart',
            message: t('metadata.status.endDateBeforeStartError'),
          });
          continue;
        }
      }

      // Check for overlaps with other ranges
      for (let j = 0; j < statusValue.ranges.length; j++) {
        if (i === j) continue;
        const otherRange = statusValue.ranges[j];
        if (
          rangesOverlap(
            { startDate: range.startDate, endDate: range.endDate },
            { startDate: otherRange.startDate, endDate: otherRange.endDate }
          )
        ) {
          errors.set(range.id, {
            type: 'overlap',
            message: t('metadata.status.rangesOverlapError'),
          });
          break;
        }
      }
    }

    return { errors, disabledDatesForRange };
  }, [statusValue.ranges, t]);

  // Handle range operations
  const handleAddRange = useCallback(() => {
    setStatusValue((prev) => ({
      ...prev,
      ranges: [
        ...(prev.ranges || []),
        {
          id: nanoid(8),
          startDate: '',
          endDate: '',
          statusDuring: statusOptionsByColor.red[0]?.color || 'red',
          statusDuringOptionId: statusOptionsByColor.red[0]?.id,
        },
      ],
    }));
  }, [statusOptionsByColor.red]);

  const handleUpdateRange = useCallback((id: string, updates: Partial<StatusRange>) => {
    setStatusValue((prev) => ({
      ...prev,
      ranges: prev.ranges?.map((r) => (r.id === id ? { ...r, ...updates } : r)) || [],
    }));
  }, []);

  const handleRemoveRange = useCallback((id: string) => {
    setStatusValue((prev) => ({
      ...prev,
      ranges: prev.ranges?.filter((r) => r.id !== id) || [],
    }));
  }, []);

  return (
    <FormItem className="col-span-6" {...restFieldProps}>
      <Card className="border-border/60" data-testid="status-field">
        <CardContent className="space-y-3">
          {showLabel && (
            <div className="flex items-center gap-2">
              {IconComponent ? (
                <span data-testid={`status-icon-${icon}`}>
                  <IconComponent className="size-4 text-muted-foreground" />
                </span>
              ) : (
                <CircleDot className="size-4 text-muted-foreground" />
              )}
              <AutoFormLabel
                label={fieldConfigItem?.label || label}
                isRequired={isRequired}
                className="text-sm font-medium"
              />
            </div>
          )}
          {/* Mode + key settings row */}
          <div className="grid gap-3 md:grid-cols-12 md:items-center">
            <div className="space-y-1 md:col-span-4">
              <Label className="text-sm">{t('metadata.status.mode')}</Label>
              <Select
                value={statusValue.mode}
                onValueChange={(v) => handleModeChange(v as 'manual' | 'scheduled')}
                disabled={disabled}
              >
                <SelectTrigger data-testid="status-mode-select" className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual" data-testid="status-mode-manual">
                    <div className="flex items-center gap-2">
                      <Hand className="size-4 text-muted-foreground" />
                      <span>{t('metadata.status.modeManual')}</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="scheduled" data-testid="status-mode-scheduled">
                    <div className="flex items-center gap-2">
                      <CalendarClock className="size-4 text-muted-foreground" />
                      <span>{t('metadata.status.modeScheduled')}</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {statusValue.mode === 'scheduled' && (
              <>
                <div className="space-y-1 md:col-span-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">{t('metadata.status.defaultStatus')}</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <Info className="size-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-xs text-muted-foreground">
                          {t('metadata.status.defaultStatusDescription')}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Select
                    value={
                      statusValue.defaultStatusOptionId ||
                      statusOptionsByColor[statusValue.defaultStatus]?.[0]?.id ||
                      statusValue.defaultStatus
                    }
                    onValueChange={(v) => {
                      const option =
                        statusOptions.find((opt) => opt.id === v) ||
                        statusOptionsByColor[v as StatusColor]?.[0];
                      if (!option) return;
                      handleDefaultStatusChange(option);
                    }}
                    disabled={disabled}
                  >
                    <SelectTrigger data-testid="status-default-select" className="h-9 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((opt) => (
                        <SelectItem
                          key={opt.id || `${opt.color}-${opt.labelDe}`}
                          value={opt.id || opt.color}
                        >
                          <div className="flex items-center gap-2">
                            {IconComponent ? (
                              <IconComponent className={`size-4 ${colorTextClasses[opt.color]}`} />
                            ) : (
                              <span className={`w-2 h-2 rounded-full ${colorClasses[opt.color]}`} />
                            )}
                            <span>{getLabel(opt)}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1 md:col-span-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">{t('metadata.status.currentStatus')}</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <Info className="size-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-xs text-muted-foreground">
                          {t('metadata.status.currentStatusDescription')}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <CurrentStatusIndicator
                    statusValue={statusValue}
                    statusOptions={statusOptions}
                    getLabel={getLabel}
                    icon={icon}
                  />
                </div>
              </>
            )}
          </div>

          {/* Manual Mode */}
          {statusValue.mode === 'manual' && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {statusOptions.map((opt, index) => (
                  <button
                    key={opt.id || `${opt.color}-${opt.labelDe}`}
                    type="button"
                    onClick={() => handleManualStatusChange(opt)}
                    disabled={disabled}
                    data-testid={statusOptionTestIds[index]}
                    data-state={
                      statusValue.manualStatusOptionId
                        ? statusValue.manualStatusOptionId === opt.id
                          ? 'checked'
                          : 'unchecked'
                        : statusValue.manualStatus === opt.color
                          ? 'checked'
                          : 'unchecked'
                    }
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md border transition-colors ${
                      (
                        statusValue.manualStatusOptionId
                          ? statusValue.manualStatusOptionId === opt.id
                          : statusValue.manualStatus === opt.color
                      )
                        ? selectedButtonClass
                        : unselectedButtonClass
                    }`}
                  >
                    {IconComponent ? (
                      <IconComponent className={`size-4 ${colorTextClasses[opt.color]}`} />
                    ) : (
                      <span className={`w-2.5 h-2.5 rounded-full ${colorClasses[opt.color]}`} />
                    )}
                    <span className="text-sm font-medium">{getLabel(opt)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Scheduled Mode */}
          {statusValue.mode === 'scheduled' && (
            <div className="space-y-3">
              {/* Ranges */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label className="text-sm">{t('metadata.status.ranges')}</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddRange}
                    disabled={disabled}
                    data-testid="add-range-button"
                  >
                    <Plus className="size-4 mr-2" />
                    {t('metadata.status.addRange')}
                  </Button>
                </div>

                {(!statusValue.ranges || statusValue.ranges.length === 0) && (
                  <p className="text-sm text-muted-foreground py-2">
                    {t('metadata.status.noRanges')}
                  </p>
                )}

                {statusValue.ranges && statusValue.ranges.length > 0 && (
                  <div className="hidden md:grid md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 px-2">
                    <span className="text-xs font-medium text-muted-foreground pl-0">
                      {t('metadata.status.rangeStartDate')}
                      <span className="text-destructive"> *</span>
                    </span>
                    <span className="text-xs font-medium text-muted-foreground pl-1">
                      {t('metadata.status.rangeEndDate')}
                      <span className="text-destructive"> *</span>
                    </span>
                    <span className="text-xs font-medium text-muted-foreground pl-2">
                      {t('metadata.status.statusDuring')}
                      <span className="text-destructive"> *</span>
                    </span>
                    <span className="text-xs font-medium text-muted-foreground pl-3">
                      {t('metadata.status.fallbackAfter')}
                    </span>
                    <span className="text-xs font-medium text-muted-foreground text-right">
                      {t('common.actions')}
                    </span>
                  </div>
                )}

                {statusValue.ranges?.map((range, index) => (
                  <StatusRangeRow
                    key={range.id}
                    range={range}
                    index={index}
                    statusOptions={statusOptions}
                    statusOptionsByColor={statusOptionsByColor}
                    onUpdate={handleUpdateRange}
                    onRemove={handleRemoveRange}
                    icon={icon}
                    disabled={disabled}
                    compact={true}
                    validationError={
                      shouldShowValidationErrors ? rangeValidation.errors.get(range.id) : undefined
                    }
                    disabledDates={rangeValidation.disabledDatesForRange.get(range.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Custom form-level error message with localization */}
          <FormMessageWithTranslation fieldPath={fieldPath} />
        </CardContent>
      </Card>
    </FormItem>
  );
}

/**
 * Custom FormMessage that translates zod validation error codes to localized messages
 */
function FormMessageWithTranslation({ fieldPath }: { fieldPath: string }) {
  const { t } = useIntl();
  const { formState } = useFormContext();

  // Navigate to the error for this field
  const pathParts = fieldPath.split('.');
  let error: { message?: string } | undefined;
  let current: Record<string, unknown> = formState.errors as Record<string, unknown>;
  for (const part of pathParts) {
    if (!current || typeof current !== 'object') break;
    current = current[part] as Record<string, unknown>;
  }
  if (current && typeof current === 'object' && 'message' in current) {
    error = current as { message?: string };
  }

  if (!error?.message) return null;

  // Map error codes to translation keys
  const errorMessage = (() => {
    switch (error.message) {
      case 'STATUS_INCOMPLETE_RANGES':
        return t('metadata.status.rangeIncompleteError');
      case 'STATUS_END_BEFORE_START':
        return t('metadata.status.endDateBeforeStartError');
      case 'STATUS_RANGES_OVERLAP':
        return t('metadata.status.rangesOverlapError');
      default:
        // If it's not a code, display as-is (fallback for other errors)
        return error.message;
    }
  })();

  return (
    <p className="text-[0.8rem] font-medium text-destructive" data-testid="status-form-error">
      {errorMessage}
    </p>
  );
}
